'use client'

import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdEdit,
    MdDelete,
    MdBeachAccess,
    MdWarning,
} from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import ConfirmModal from '@/components/confirm-modal'
import SearchableSelect from '@/components/searchable-select'
import {
    getVacations,
    getExpiringVacations,
    createVacation,
    updateVacation,
    deleteVacation,
    HrVacation,
    ExpiringVacation,
} from '@/lib/api/hr/hr-vacation-api'
import {
    getHrEmployees,
    HrEmployee,
} from '@/lib/api/hr/hr-employee-api'

const STATUS_BADGES: Record<string, string> = {
    SCHEDULED: 'badge-info',
    IN_PROGRESS: 'badge-warning',
    COMPLETED: 'badge-success',
    CANCELLED: 'badge-error',
}

const STATUS_LABELS: Record<string, string> = {
    SCHEDULED: 'Agendada',
    IN_PROGRESS: 'Em Andamento',
    COMPLETED: 'Concluída',
    CANCELLED: 'Cancelada',
}

interface VacationForm {
    employeeId: string
    acquisitivePeriodStart: string
    acquisitivePeriodEnd: string
    daysEntitled: string
    startDate: string
    endDate: string
    daysUsed: string
    pecuniaryAbatement: boolean
    pecuniaryDays: string
    status: string
}

const initialForm: VacationForm = {
    employeeId: '',
    acquisitivePeriodStart: '',
    acquisitivePeriodEnd: '',
    daysEntitled: '30',
    startDate: '',
    endDate: '',
    daysUsed: '',
    pecuniaryAbatement: false,
    pecuniaryDays: '',
    status: 'SCHEDULED',
}

const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('pt-BR')
}

export default function VacationsPage() {
    const { user } = useAuth()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('hr', 'vacations')

    const [vacations, setVacations] = useState<HrVacation[]>([])
    const [expiring, setExpiring] = useState<ExpiringVacation[]>([])
    const [employees, setEmployees] = useState<HrEmployee[]>([])
    const [loading, setLoading] = useState(true)

    // Modal
    const modalRef = useRef<HTMLDialogElement>(null)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [form, setForm] = useState<VacationForm>(initialForm)
    const [saving, setSaving] = useState(false)

    // Delete
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        loadAll()
    }, [])

    const loadAll = async () => {
        setLoading(true)
        try {
            const [vacRes, expRes, empRes] = await Promise.all([
                getVacations(),
                getExpiringVacations(),
                getHrEmployees({ limit: 500 }),
            ])

            if (vacRes.success && vacRes.result) {
                setVacations(vacRes.result)
            }
            if (expRes.success && expRes.result) {
                setExpiring(expRes.result)
            }
            if (empRes.success && empRes.result) {
                setEmployees(Array.isArray(empRes.result) ? empRes.result : [])
            }
        } catch {
            toast.error('Erro ao carregar dados de férias')
        } finally {
            setLoading(false)
        }
    }

    const openCreateModal = () => {
        setEditingId(null)
        setForm(initialForm)
        modalRef.current?.showModal()
    }

    const openEditModal = (vacation: HrVacation) => {
        setEditingId(vacation.id)
        setForm({
            employeeId: String(vacation.employeeId),
            acquisitivePeriodStart: vacation.acquisitivePeriodStart?.split('T')[0] || '',
            acquisitivePeriodEnd: vacation.acquisitivePeriodEnd?.split('T')[0] || '',
            daysEntitled: String(vacation.daysEntitled),
            startDate: vacation.startDate?.split('T')[0] || '',
            endDate: vacation.endDate?.split('T')[0] || '',
            daysUsed: vacation.daysUsed != null ? String(vacation.daysUsed) : '',
            pecuniaryAbatement: vacation.pecuniaryAbatement,
            pecuniaryDays: vacation.pecuniaryDays != null ? String(vacation.pecuniaryDays) : '',
            status: vacation.status,
        })
        modalRef.current?.showModal()
    }

    const closeModal = () => {
        modalRef.current?.close()
        setEditingId(null)
        setForm(initialForm)
    }

    const handleSave = async () => {
        if (!form.employeeId || !form.acquisitivePeriodStart || !form.acquisitivePeriodEnd) {
            toast.warning('Preencha os campos obrigatórios')
            return
        }

        setSaving(true)
        try {
            const data: any = {
                employeeId: parseInt(form.employeeId, 10),
                acquisitivePeriodStart: form.acquisitivePeriodStart,
                acquisitivePeriodEnd: form.acquisitivePeriodEnd,
                daysEntitled: parseInt(form.daysEntitled, 10) || 30,
                startDate: form.startDate || null,
                endDate: form.endDate || null,
                daysUsed: form.daysUsed ? parseInt(form.daysUsed, 10) : null,
                pecuniaryAbatement: form.pecuniaryAbatement,
                pecuniaryDays: form.pecuniaryDays ? parseInt(form.pecuniaryDays, 10) : null,
            }

            if (editingId) {
                data.status = form.status
                const res = await updateVacation(editingId, data)
                if (res.success) {
                    toast.success('Férias atualizadas com sucesso')
                    closeModal()
                    loadAll()
                } else {
                    toast.error(res.error || 'Erro ao atualizar férias')
                }
            } else {
                const res = await createVacation(data)
                if (res.success) {
                    toast.success('Férias agendadas com sucesso')
                    closeModal()
                    loadAll()
                } else {
                    toast.error(res.error || 'Erro ao agendar férias')
                }
            }
        } catch {
            toast.error('Erro ao salvar férias')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteId) return
        setDeleting(true)
        try {
            const res = await deleteVacation(deleteId)
            if (res.success) {
                toast.success('Férias excluídas com sucesso')
                setDeleteId(null)
                loadAll()
            } else {
                toast.error(res.error || 'Erro ao excluir férias')
            }
        } catch {
            toast.error('Erro ao excluir férias')
        } finally {
            setDeleting(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando férias...</p>
            </div>
        )
    }

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-warning to-warning/70 flex items-center justify-center shadow-lg">
                        <MdBeachAccess className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Férias
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {vacations.length} {vacations.length === 1 ? 'registro' : 'registros'}
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button className="btn btn-secondary gap-2" onClick={openCreateModal}>
                        <MdAdd className="text-xl" />
                        <span className="hidden sm:inline">Agendar Férias</span>
                    </button>
                )}
            </div>

            {/* Expiring Vacations Alert */}
            {expiring.length > 0 && (
                <div className="mb-6">
                    <div className="alert alert-warning shadow-sm">
                        <MdWarning className="text-2xl shrink-0" />
                        <div>
                            <h3 className="font-bold">Férias prestes a vencer</h3>
                            <p className="text-sm">
                                {expiring.length} funcionário{expiring.length !== 1 ? 's' : ''} com
                                férias prestes a vencer o período concessivo
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                        {expiring.map((exp, idx) => (
                            <div
                                key={idx}
                                className="card bg-warning/10 border border-warning/30 shadow-sm"
                            >
                                <div className="card-body p-4">
                                    <h4 className="font-semibold text-sm">{exp.employeeName}</h4>
                                    {exp.department && (
                                        <p className="text-xs text-base-content/60">{exp.department}</p>
                                    )}
                                    <div className="text-xs mt-1">
                                        <span className="text-base-content/60">Período aquisitivo: </span>
                                        <span className="font-mono">
                                            {formatDate(exp.acquisitivePeriodStart)} - {formatDate(exp.acquisitivePeriodEnd)}
                                        </span>
                                    </div>
                                    <div className="text-xs">
                                        <span className="text-base-content/60">Vence em: </span>
                                        <span className="font-semibold text-warning">
                                            {formatDate(exp.concessiveEnd)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Table */}
            {vacations.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdBeachAccess className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhuma férias registrada
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            Agende férias para seus funcionários
                        </p>
                        {canCreate && (
                            <button onClick={openCreateModal} className="btn btn-secondary gap-2">
                                <MdAdd className="text-xl" />
                                Agendar Férias
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-300/50">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Funcionário</th>
                                <th>Período Aquisitivo</th>
                                <th className="text-center">Dias</th>
                                <th>Início</th>
                                <th>Fim</th>
                                <th className="text-center">Abono</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {vacations.map(vacation => (
                                <tr key={vacation.id} className="hover">
                                    <td>
                                        <div className="font-semibold">
                                            {vacation.employee?.name || '-'}
                                        </div>
                                        {vacation.employee?.department && (
                                            <div className="text-xs text-base-content/60">
                                                {vacation.employee.department}
                                            </div>
                                        )}
                                    </td>
                                    <td className="font-mono text-sm">
                                        {formatDate(vacation.acquisitivePeriodStart)} - {formatDate(vacation.acquisitivePeriodEnd)}
                                    </td>
                                    <td className="text-center">
                                        <span className="badge badge-ghost badge-sm">
                                            {vacation.daysUsed ?? vacation.daysEntitled}
                                        </span>
                                    </td>
                                    <td>{formatDate(vacation.startDate)}</td>
                                    <td>{formatDate(vacation.endDate)}</td>
                                    <td className="text-center">
                                        {vacation.pecuniaryAbatement ? (
                                            <span className="badge badge-info badge-sm">
                                                {vacation.pecuniaryDays || 10} dias
                                            </span>
                                        ) : (
                                            <span className="text-base-content/40">-</span>
                                        )}
                                    </td>
                                    <td>
                                        <span className={`badge ${STATUS_BADGES[vacation.status] || 'badge-ghost'}`}>
                                            {STATUS_LABELS[vacation.status] || vacation.status}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="flex items-center gap-1 justify-end">
                                            {canUpdate && (
                                                <button
                                                    onClick={() => openEditModal(vacation)}
                                                    className="btn btn-ghost btn-xs"
                                                    title="Editar"
                                                >
                                                    <MdEdit className="text-lg" />
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    onClick={() => setDeleteId(vacation.id)}
                                                    className="btn btn-ghost btn-xs text-error"
                                                    title="Excluir"
                                                >
                                                    <MdDelete className="text-lg" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Create/Edit Modal */}
            <dialog ref={modalRef} className="modal">
                <div className="modal-box !max-w-lg">
                    <h3 className="font-bold text-lg mb-4">
                        {editingId ? 'Editar Férias' : 'Agendar Férias'}
                    </h3>

                    <div className="space-y-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Funcionário *</span>
                            </label>
                            <SearchableSelect
                                options={employees.map(emp => ({
                                    value: String(emp.id),
                                    label: emp.name,
                                    description: emp.department || undefined,
                                }))}
                                value={form.employeeId}
                                onChange={value => setForm(prev => ({ ...prev, employeeId: value }))}
                                placeholder="Selecione um funcionário"
                                disabled={!!editingId}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Início Aquisitivo *</span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={form.acquisitivePeriodStart}
                                    onChange={e => setForm(prev => ({ ...prev, acquisitivePeriodStart: e.target.value }))}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Fim Aquisitivo *</span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={form.acquisitivePeriodEnd}
                                    onChange={e => setForm(prev => ({ ...prev, acquisitivePeriodEnd: e.target.value }))}
                                />
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Dias de Direito</span>
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="30"
                                className="input input-bordered w-full"
                                value={form.daysEntitled}
                                onChange={e => setForm(prev => ({ ...prev, daysEntitled: e.target.value }))}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Data Início</span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={form.startDate}
                                    onChange={e => setForm(prev => ({ ...prev, startDate: e.target.value }))}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Data Fim</span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={form.endDate}
                                    onChange={e => setForm(prev => ({ ...prev, endDate: e.target.value }))}
                                />
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Dias Usados</span>
                            </label>
                            <input
                                type="number"
                                min="0"
                                max="30"
                                className="input input-bordered w-full"
                                placeholder="Preencher ao concluir"
                                value={form.daysUsed}
                                onChange={e => setForm(prev => ({ ...prev, daysUsed: e.target.value }))}
                            />
                        </div>

                        <div className="form-control">
                            <label className="label cursor-pointer justify-start gap-3">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-sm checkbox-secondary"
                                    checked={form.pecuniaryAbatement}
                                    onChange={e => setForm(prev => ({ ...prev, pecuniaryAbatement: e.target.checked }))}
                                />
                                <span className="label-text">Abono pecuniário</span>
                            </label>
                        </div>

                        {form.pecuniaryAbatement && (
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Dias de Abono</span>
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="10"
                                    className="input input-bordered w-full"
                                    placeholder="10"
                                    value={form.pecuniaryDays}
                                    onChange={e => setForm(prev => ({ ...prev, pecuniaryDays: e.target.value }))}
                                />
                            </div>
                        )}

                        {editingId && (
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Status</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={form.status}
                                    onChange={e => setForm(prev => ({ ...prev, status: e.target.value }))}
                                >
                                    {Object.entries(STATUS_LABELS).map(([key, label]) => (
                                        <option key={key} value={key}>{label}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    <div className="modal-action">
                        <button className="btn btn-ghost" onClick={closeModal} disabled={saving}>
                            Cancelar
                        </button>
                        <button className="btn btn-secondary" onClick={handleSave} disabled={saving}>
                            {saving && <span className="loading loading-spinner loading-sm"></span>}
                            {editingId ? 'Salvar' : 'Agendar'}
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={closeModal}>close</button>
                </form>
            </dialog>

            {/* Delete confirmation */}
            <ConfirmModal
                isOpen={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Excluir Férias"
                message="Tem certeza de que deseja excluir este registro de férias? Esta ação não pode ser desfeita."
                loading={deleting}
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
